<template>
  <AuthLayout
    :title="
      registered ? $t('auth.register.doneTitle') : $t('auth.register.title')
    "
    :subtitle="registered ? '' : $t('auth.register.subtitle')"
  >
    <!-- Success: the account exists but cannot sign in until it is verified,
         so this state has to be unmissable rather than a passing toast. -->
    <div v-if="registered" role="status" class="flex flex-col gap-4">
      <p class="text-muted leading-relaxed">
        {{ $t("auth.register.doneBody", { email: submittedEmail }) }}
      </p>
      <p class="text-subtle text-sm leading-relaxed">
        {{ $t("auth.register.doneHint") }}
      </p>
    </div>

    <form v-else class="flex flex-col gap-5" @submit.prevent="submit">
      <HoneypotField ref="honeypot" />
      <InputField
        v-model="form.name"
        :label="$t('account.name')"
        name="name"
        autocomplete="name"
        required
        :placeholder="$t('auth.register.namePlaceholder')"
      />
      <InputField
        v-model="form.email"
        :label="$t('account.email')"
        type="email"
        name="email"
        inputmode="email"
        autocomplete="email"
        required
        :placeholder="$t('auth.register.emailPlaceholder')"
      />
      <div>
        <InputField
          v-model="form.password"
          :label="$t('auth.register.password')"
          type="password"
          name="password"
          autocomplete="new-password"
          required
          :placeholder="$t('auth.register.passwordPlaceholder')"
        />
        <p class="text-subtle text-xs mt-1.5">
          {{ $t("auth.register.passwordRule", { count: minLength }) }}
        </p>
      </div>
      <InputField
        v-model="form.confirm"
        :label="$t('account.confirmPassword')"
        type="password"
        name="password_confirmation"
        autocomplete="new-password"
        required
      />

      <Button class="mt-2 w-full text-lg" :loading="loading">
        {{ $t("auth.register.submit") }}
      </Button>
      <ErrorLabel :error="errorMessage" />

      <p class="text-sm font-light">
        <span class="text-muted">{{ $t("auth.register.haveAccount") }}</span>
        <router-link to="/login" class="text-highlight hover:underline">
          {{ $t("auth.register.signIn") }}
        </router-link>
      </p>
    </form>
  </AuthLayout>
</template>

<script>
import AuthLayout from "@/components/auth/AuthLayout.vue";
import Button from "@/components/common/Button.vue";
import ErrorLabel from "@/components/common/ErrorLabel.vue";
import HoneypotField from "@/components/common/HoneypotField.vue";

const MIN_PASSWORD_LENGTH = 6;

export default {
  name: "Register",
  components: { AuthLayout, Button, ErrorLabel, HoneypotField },

  data() {
    return {
      form: { name: "", email: "", password: "", confirm: "" },
      loading: false,
      registered: false,
      submittedEmail: "",
      errorMessage: "",
      minLength: MIN_PASSWORD_LENGTH,
    };
  },

  methods: {
    /** Check locally first so an obvious typo never costs a round trip. */
    validate() {
      if (this.form.password.length < MIN_PASSWORD_LENGTH) {
        return this.$t("auth.error.passwordValidation").toString();
      }
      if (this.form.password !== this.form.confirm) {
        return this.$t("auth.error.passwordMatch").toString();
      }
      return "";
    },

    submit() {
      if (this.loading) return;

      const validationError = this.validate();
      if (validationError) {
        this.errorMessage = validationError;
        return;
      }

      this.loading = true;
      this.errorMessage = "";
      const email = this.form.email.trim();

      this.$store
        .dispatch("register", {
          name: this.form.name.trim(),
          email,
          password: this.form.password,
          password_confirmation: this.form.confirm,
          locale: this.$root.$i18n.locale,
          ...this.$refs.honeypot.payload(),
        })
        .then(() => {
          this.submittedEmail = email;
          this.registered = true;
        })
        .catch((error) => {
          this.errorMessage = this.messageFor(error);
        })
        .finally(() => (this.loading = false));
    },

    messageFor(error) {
      // 422 carries per-field messages — "email already taken" is the common
      // one and far more useful than a generic failure.
      const firstField = Object.values(error?.response?.data?.errors ?? {})[0];
      if (Array.isArray(firstField) && firstField.length) return firstField[0];
      return this.$t("auth.register.error").toString();
    },
  },
};
</script>
